/**
 * Same-tab token-rotation registry (PROD-SESSION-HARD-NAV-01 / F1.3).
 *
 * lib/api.ts rotates the token; stores/authStore.ts has to learn about it.
 * A direct call is required rather than a broadcast, because BroadcastChannel
 * never delivers to the context that posted -- so the tab that refreshed would
 * otherwise be the one tab that never learns its own new token, which is
 * exactly how `auth-storage` drifted away from `access_token`.
 *
 * lib/api.ts cannot import the store (the store imports lib/api -- a cycle),
 * so the two meet here, the same way lib/authBroadcast.ts exists so both can
 * share a channel.
 *
 * Why its OWN module rather than a listener slot on `__authInternals`: the
 * store has to register at MODULE SCOPE (a rotation fired by the boot refresh
 * in onRehydrateStorage happens before any component mounts). Reaching into
 * `@/lib/api` at module scope makes importing the store impossible for any
 * test that `vi.mock('@/lib/api')` with only the exports it uses -- 69 files
 * mock the module, 64 of them declare no `__authInternals`, and the 42 of
 * those that reach this store through `tests/test-utils.tsx` failed at
 * COLLECTION with "No __authInternals export is defined on the @/lib/api
 * mock". This module has no imports of its own, so it stays loadable under any
 * mock of anything.
 */

export interface TokenPair {
  accessToken: string
  refreshToken: string | null
}

export type TokenSink = (pair: TokenPair) => void

const sinks = new Set<TokenSink>()

/**
 * Observe every same-tab token rotation. Returns an unregister function.
 *
 * Safe to call at module scope: nothing here touches the network, storage or
 * `window`.
 */
export function registerTokenSink(sink: TokenSink): () => void {
  sinks.add(sink)
  return () => {
    sinks.delete(sink)
  }
}

/**
 * Hand a freshly rotated pair to every sink.
 *
 * A throwing sink must never turn a successful refresh into a failed one
 * (which would force-logout in the interceptor's catch), and must not stop the
 * remaining sinks from being told, so each call is isolated.
 */
export function publishTokenPair(pair: TokenPair): void {
  for (const sink of sinks) {
    try {
      sink(pair)
    } catch {
      // Deliberately swallowed -- see above.
    }
  }
}

// ----------------------------------------------------------------------------
// Auth teardown (review #1081 R4)
// ----------------------------------------------------------------------------
// The mirror image of the token hand-off: lib/api.ts also CLEARS auth (the
// forced logout after a refusable refresh), and the store has to know, for the
// same cycle reason it cannot be imported. A tab never receives its own
// BroadcastChannel message, so the LOGGED_OUT broadcast does not cover this.
//
// The store uses it to advance a logout epoch, so a response that was already
// in flight when the session ended cannot write itself back in afterwards.
// ----------------------------------------------------------------------------

export type AuthClearedSink = () => void

const clearedSinks = new Set<AuthClearedSink>()

/** Observe every same-tab auth teardown. Returns an unregister function. */
export function registerAuthClearedSink(sink: AuthClearedSink): () => void {
  clearedSinks.add(sink)
  return () => {
    clearedSinks.delete(sink)
  }
}

/** Tell every sink that the credentials in this context are gone. */
export function publishAuthCleared(): void {
  for (const sink of clearedSinks) {
    try {
      sink()
    } catch {
      // A throwing sink must not stop the teardown, nor the other sinks.
    }
  }
}

/** Test-only: drop every registration between cases. */
export function resetForTests(): void {
  sinks.clear()
  clearedSinks.clear()
}
